import React, { useMemo, useState, useCallback } from 'react';
import {
  MapContainer,
  TileLayer,
  CircleMarker,
  Marker,
  Popup,
  useMap,
  useMapEvents,
} from 'react-leaflet';
import L from 'leaflet';
import Supercluster from 'supercluster';
import 'leaflet/dist/leaflet.css';
import { useLanguage } from '../../i18n';

// Types for pothole features
interface PotholeProperties {
  id: number;
  street: string;
  city: string;
  reliability: number;
  created: string;
}

interface PotholeFeature {
  type: 'Feature';
  geometry: {
    type: 'Point';
    coordinates: [number, number]; // [lng, lat]
  };
  properties: PotholeProperties;
}

interface ClusterProperties {
  cluster: true;
  cluster_id: number;
  point_count: number;
  point_count_abbreviated: string | number;
}

type SuperclusterFeature = PotholeFeature | {
  type: 'Feature';
  geometry: { type: 'Point'; coordinates: [number, number] };
  properties: ClusterProperties;
};

type ViewMode = 'heatmap' | 'points';

interface PotholeMapProps {
  features?: PotholeFeature[];
  center?: [number, number];
  zoom?: number;
  mode?: ViewMode;
}

// Component to update map view when center changes
const MapUpdater: React.FC<{ center: [number, number]; zoom: number }> = ({
  center,
  zoom,
}) => {
  const map = useMap();
  React.useEffect(() => {
    map.setView(center, zoom);
  }, [map, center, zoom]);
  return null;
};

// Color based on reliability
const getReliabilityColor = (reliability: number): string => {
  if (reliability >= 8) return '#ff4444';
  if (reliability >= 5) return '#ff8844';
  return '#ffaa44';
};

// Color for clusters based on point count - more distinct gradient
const getClusterColor = (count: number): string => {
  if (count >= 15) return '#d32f2f'; // dark red
  if (count >= 10) return '#f44336'; // red
  if (count >= 5) return '#ff9800';  // orange
  if (count >= 3) return '#ffc107';  // amber
  return '#4caf50'; // green for small clusters
};

// Create a custom cluster icon with the count displayed
const createClusterIcon = (count: number): L.DivIcon => {
  const color = getClusterColor(count);
  // Minimum size of 36px to ensure numbers are always visible
  const size = Math.min(70, Math.max(36, 30 + Math.sqrt(count) * 6));
  const fontSize = count >= 100 ? 11 : count >= 10 ? 13 : 14;

  return L.divIcon({
    html: `<div style="
      background-color: ${color};
      width: ${size}px;
      height: ${size}px;
      border-radius: 50%;
      border: 3px solid rgba(255,255,255,0.8);
      display: flex;
      align-items: center;
      justify-content: center;
      color: white;
      font-weight: bold;
      font-size: ${fontSize}px;
      text-shadow: 1px 1px 2px rgba(0,0,0,0.7);
      box-shadow: 0 3px 8px rgba(0,0,0,0.4);
      font-family: Arial, sans-serif;
    ">${count}</div>`,
    className: 'cluster-marker',
    iconSize: [size, size],
    iconAnchor: [size / 2, size / 2],
  });
};

const formatDate = (dateString: string): string => {
  return new Date(dateString).toLocaleString();
};

// Cluster layer component that handles zoom events
const ClusterLayer: React.FC<{
  features: PotholeFeature[];
  supercluster: Supercluster<PotholeProperties, ClusterProperties>;
  t: (key: any, params?: Record<string, string | number>) => string;
}> = ({ features, supercluster, t }) => {
  const map = useMap();
  const [clusters, setClusters] = useState<SuperclusterFeature[]>([]);
  const [zoom, setZoom] = useState(map.getZoom());

  const updateClusters = useCallback(() => {
    const bounds = map.getBounds();
    const bbox: [number, number, number, number] = [
      bounds.getWest(),
      bounds.getSouth(),
      bounds.getEast(),
      bounds.getNorth(),
    ];
    const currentZoom = Math.floor(map.getZoom());
    setZoom(currentZoom);

    const newClusters = supercluster.getClusters(bbox, currentZoom);
    setClusters(newClusters as SuperclusterFeature[]);
  }, [map, supercluster]);

  useMapEvents({
    zoomend: updateClusters,
    moveend: updateClusters,
  });

  React.useEffect(() => {
    updateClusters();
  }, [updateClusters, features]);

  const handleClusterClick = (clusterId: number, coords: [number, number]) => {
    const expansionZoom = Math.min(
      supercluster.getClusterExpansionZoom(clusterId),
      18
    );
    map.setView([coords[1], coords[0]], expansionZoom);
  };

  return (
    <>
      {clusters.map((feature, index) => {
        const [lng, lat] = feature.geometry.coordinates;
        const isCluster = (feature.properties as ClusterProperties).cluster;

        if (isCluster) {
          // Cluster with multiple points
          const props = feature.properties as ClusterProperties;

          return (
            <Marker
              key={`cluster-${props.cluster_id}`}
              position={[lat, lng]}
              icon={createClusterIcon(props.point_count)}
              eventHandlers={{
                click: () => handleClusterClick(props.cluster_id, [lng, lat]),
              }}
            >
              <Popup>
                <div style={{ textAlign: 'center', minWidth: 100 }}>
                  <strong style={{ fontSize: '16px' }}>
                    {props.point_count} {t('map.potholes')}
                  </strong>
                  <br />
                  <span style={{ color: '#666', fontSize: '12px' }}>
                    {t('map.inThisArea')}
                  </span>
                  <br />
                  <span style={{ color: '#888', fontSize: '11px' }}>
                    {t('map.clickToZoom')}
                  </span>
                </div>
              </Popup>
            </Marker>
          );
        } else {
          // Single point - still show as a bubble with "1" for consistency
          const props = feature.properties as PotholeProperties;
          return (
            <Marker
              key={`single-${props.id}-${index}`}
              position={[lat, lng]}
              icon={createClusterIcon(1)}
            >
              <Popup>
                <div style={{ minWidth: 150 }}>
                  <strong style={{ fontSize: '14px' }}>{props.street}</strong>
                  <br />
                  <span style={{ color: '#666' }}>{props.city}</span>
                  <hr
                    style={{
                      margin: '8px 0',
                      border: 'none',
                      borderTop: '1px solid #eee',
                    }}
                  />
                  <div style={{ fontSize: '13px' }}>
                    <div style={{ marginBottom: '4px' }}>
                      <strong>{t('map.reliability')}:</strong> {props.reliability}/10
                    </div>
                    <div>
                      <strong>{t('map.reported')}:</strong>
                      <br />
                      <span style={{ fontSize: '12px', color: '#666' }}>
                        {formatDate(props.created)}
                      </span>
                    </div>
                  </div>
                </div>
              </Popup>
            </Marker>
          );
        }
      })}
    </>
  );
};

// Simple points layer (no clustering)
const PointsLayer: React.FC<{
  features: PotholeFeature[];
  t: (key: any, params?: Record<string, string | number>) => string;
}> = ({ features, t }) => {
  return (
    <>
      {features.map((feature, index) => {
        const [lng, lat] = feature.geometry.coordinates;
        const props = feature.properties;

        return (
          <CircleMarker
            key={`point-${props.id}-${index}`}
            center={[lat, lng]}
            radius={7}
            pathOptions={{
              fillColor: getReliabilityColor(props.reliability),
              color: '#fff',
              weight: 2,
              opacity: 1,
              fillOpacity: 0.9,
            }}
          >
            <Popup>
              <div style={{ minWidth: 150 }}>
                <strong style={{ fontSize: '14px' }}>{props.street}</strong>
                <br />
                <span style={{ color: '#666' }}>{props.city}</span>
                <hr
                  style={{
                    margin: '8px 0',
                    border: 'none',
                    borderTop: '1px solid #eee',
                  }}
                />
                <div style={{ fontSize: '13px' }}>
                  <div style={{ marginBottom: '4px' }}>
                    <strong>{t('map.reliability')}:</strong> {props.reliability}/10
                  </div>
                  <div>
                    <strong>{t('map.reported')}:</strong>
                    <br />
                    <span style={{ fontSize: '12px', color: '#666' }}>
                      {formatDate(props.created)}
                    </span>
                  </div>
                </div>
              </div>
            </Popup>
          </CircleMarker>
        );
      })}
    </>
  );
};

const PotholeMap: React.FC<PotholeMapProps> = ({
  features = [],
  center = [56.9496, 24.1052],
  zoom = 12,
  mode = 'heatmap',
}) => {
  const { t } = useLanguage();

  // Create supercluster instance for heatmap mode
  const supercluster = useMemo(() => {
    const sc = new Supercluster<PotholeProperties, ClusterProperties>({
      radius: 80,
      maxZoom: 16,
      minZoom: 0,
    });

    if (features.length > 0) {
      sc.load(features);
    }

    return sc;
  }, [features]);

  return (
    <MapContainer
      center={center}
      zoom={zoom}
      style={{ height: '100%', width: '100%' }}
      scrollWheelZoom={true}
    >
      <TileLayer
        attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors'
        url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
      />
      <MapUpdater center={center} zoom={zoom} />

      {features.length > 0 && mode === 'heatmap' && (
        <ClusterLayer features={features} supercluster={supercluster} t={t} />
      )}

      {features.length > 0 && mode === 'points' && (
        <PointsLayer features={features} t={t} />
      )}
    </MapContainer>
  );
};

export default PotholeMap;
